import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { color } from '@/colors';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { PlusIcon } from '@/UI/Icons/PlusIcon';
import { Flex, FlexCenHorVer, FlexCenVer, FlexCol } from '@/UI/styles';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { useIsHovered } from '@/hooks/useIsHovered';
import { thumbnailSource } from '@/utils/assetUrls';
import { AssetThumbnail } from '@/components/Common/AssetThumbnail';
import { useResolvedAssets } from '@/state/workflow.state';
import { useMediaIterator } from './useMediaIterator';
import type { FileType } from '@/hooks/useUploadMediaAsset';
import type { FileKind } from 'web';
import type { MediaIteratorInput } from '@/types/design-app.types';

const THUMBNAIL_SIZE = 68;

interface DesignAppMediaIteratorProps {
  id: string;
  value: MediaIteratorInput['value'];
  onChange: (id: string, value: MediaIteratorInput['value']) => void;
  acceptedFileType: FileType;
  disabled: boolean;
  isLoading: boolean;
  setIsUploading: (isUploading: boolean) => void;
  error: boolean;
  helperText: string | null;
}

export default function DesignAppMediaIterator({
  id,
  value,
  onChange,
  acceptedFileType,
  disabled,
  isLoading,
  setIsUploading,
  error,
  helperText,
}: DesignAppMediaIteratorProps) {
  const { files, maxFiles, canAdd, handleRemove, getRootProps, getInputProps, open, isInProgress } = useMediaIterator({
    id,
    value,
    onChange,
    acceptedFileType,
    disabled,
    isLoading,
    setIsUploading,
  });

  if (isLoading) return null;

  return (
    <FlexCol
      sx={{
        width: '100%',
        gap: 1,
        maxHeight: 240,
        overflow: 'hidden auto',
        scrollbarColor: `${color.White16_T} transparent`,
      }}
    >
      <Flex
        {...getRootProps()}
        sx={{
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'flex-start',
          alignContent: 'flex-start',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <input {...getInputProps()} />

        {canAdd && (
          <FlexCenHorVer
            onClick={open}
            sx={{
              width: THUMBNAIL_SIZE,
              height: THUMBNAIL_SIZE,
              borderRadius: '4px',
              border: `1px solid ${token.border}`,
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': { borderColor: color.White40_T },
            }}
          >
            <PlusIcon style={{ width: 16, height: 16, color: token.textSecondary }} />
          </FlexCenHorVer>
        )}

        {files.map((file, index) => (
          <FileThumbnail
            key={`${file.url}-${index}`}
            file={file}
            index={index}
            onRemove={handleRemove}
            disabled={disabled}
          />
        ))}

        {isInProgress && (
          <FlexCenHorVer
            sx={{
              width: THUMBNAIL_SIZE,
              height: THUMBNAIL_SIZE,
              borderRadius: '4px',
              border: `1px solid ${token.border}`,
              flexShrink: 0,
            }}
          >
            <TextSmRg color={token.textSecondary}>...</TextSmRg>
          </FlexCenHorVer>
        )}
      </Flex>

      <FlexCenVer sx={{ justifyContent: 'flex-end' }}>
        <TextSmRg color={token.textSecondary}>
          {files.length}/{maxFiles}
        </TextSmRg>
      </FlexCenVer>

      {error && helperText && (
        <TextSmRg color={token.textDanger} sx={{ mt: 0.5 }}>
          {helperText}
        </TextSmRg>
      )}
    </FlexCol>
  );
}

function FileThumbnail({
  file,
  index,
  onRemove,
  disabled,
}: {
  file: FileKind;
  index: number;
  onRemove: (index: number) => void;
  disabled: boolean;
}) {
  const { isHovered, ...hoverProps } = useIsHovered();
  const resolvedAssets = useResolvedAssets();

  return (
    <Box
      {...hoverProps}
      sx={{
        position: 'relative',
        width: THUMBNAIL_SIZE,
        height: THUMBNAIL_SIZE,
        borderRadius: '4px',
        border: `1px solid ${token.border}`,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <AssetThumbnail
        source={thumbnailSource(file, resolvedAssets)}
        alt={`file-${index}`}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '4px' }}
      />

      {!disabled && isHovered && (
        <Box
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
          }}
        >
          <AppXBtn size={16} onClick={() => onRemove(index)} />
        </Box>
      )}
    </Box>
  );
}
